/**
 * The insured's page: finish the half of the questionnaire only they can answer.
 *
 * Reached from a link their agent sent, at /finish/:token. There is no account
 * and no sign-in — the link carries a long random token, and the PIN their agent
 * gave them separately is typed in here. Both are required on every request, so
 * a forwarded email is not enough by itself.
 *
 * This page is seen by people who are not insurance professionals and did not
 * ask to be here. So it explains who is asking and why before it asks anything,
 * says how far through they are, and saves as they type — because the one thing
 * guaranteed to lose a half-finished application is making someone start again.
 *
 * The agent's own answers are not merely hidden here: the server never sends
 * them. Only questions tagged for the insured cross the wire.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { FormAnswers, SupplementalForm as FormDef } from '@commpass/shared';
import { completeness } from '@commpass/shared';
import { SupplementalForm } from '../components/SupplementalForm';
import { ApiError, api } from '../lib/api';

type Stage = 'locked' | 'filling' | 'done';

export function InsuredFinish() {
  const { token = '' } = useParams<{ token: string }>();
  const [stage, setStage] = useState<Stage>('locked');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState<FormDef | null>(null);
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [saved, setSaved] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.insuredOpen(token, pin);
      setForm(r.form);
      setAnswers(r.answers);
      setBusinessName(r.businessName);
      setStage(r.status === 'insured_completed' ? 'done' : 'filling');
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 429
          ? 'Too many attempts — wait a minute and try again.'
          : e instanceof ApiError
            ? e.message
            : 'Something went wrong. Try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  };

  // Save on a debounce rather than a button. These are long forms filled in by
  // someone who did not choose to be doing it, often on a phone, and a lost
  // half-hour is a form that never comes back.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<FormAnswers | null>(null);
  useEffect(() => {
    if (stage !== 'filling' || !pending.current) return;
    setSaved('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const payload = pending.current;
      pending.current = null;
      if (!payload) return;
      api
        .insuredSave(token, pin, payload)
        .then(() => setSaved('saved'))
        .catch(() => setSaved('error'));
    }, 900);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [answers, stage, token, pin]);

  const set = (id: string, value: FormAnswers[string]) => {
    setAnswers((a) => {
      const next = { ...a, [id]: value };
      pending.current = next;
      return next;
    });
  };

  const finish = async () => {
    setBusy(true);
    try {
      // Flush anything still in the debounce before declaring it finished.
      if (pending.current) await api.insuredSave(token, pin, pending.current);
      await api.insuredComplete(token, pin);
      setStage('done');
    } catch {
      setError('Could not submit — please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'locked') {
    return (
      <div className="wrap insured-gate">
        <h2>Finish your insurance application</h2>
        <p className="insured-intro">
          Your insurance agent has started an application for your business and needs you to answer
          the parts only you can. It should take a few minutes, and it saves as you go.
        </p>
        <p className="insured-intro">
          Enter the code your agent gave you. It is not in the email — they will have sent it to you
          separately, or told you over the phone.
        </p>
        <div className="insured-pin">
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label="Access code"
            onKeyDown={(e) => e.key === 'Enter' && pin.length >= 4 && void open()}
          />
          <button className="btn btn-primary" disabled={pin.length < 4 || busy} onClick={() => void open()}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </div>
        {error && <div className="insured-error">{error}</div>}
        <p className="insured-foot">
          Agents Alliance Services · If you weren&apos;t expecting this, please contact your insurance
          agent before entering anything.
        </p>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div className="wrap insured-gate">
        <h2>Thank you — that&apos;s everything we need</h2>
        <p className="insured-intro">
          Your answers have gone back to your agent{businessName ? ` for ${businessName}` : ''}. They
          will be in touch about the next steps. This link has now been used and will no longer open.
        </p>
        <p className="insured-foot">Agents Alliance Services</p>
      </div>
    );
  }

  if (!form) return null;
  const progress = completeness(form, answers);

  return (
    <div className="wrap">
      <div className="insured-head">
        <h2>{businessName || form.class}</h2>
        <p className="insured-intro">
          Your agent has filled in what they could. These are the questions about how your business
          actually operates — the parts only you can answer. It saves automatically.
        </p>
      </div>

      <SupplementalForm form={form} answers={answers} onChange={set} audience="insured" />

      <div className="supp-actions">
        <div className="supp-buttons">
          <button className="btn btn-primary" disabled={busy} onClick={() => void finish()}>
            {busy ? 'Sending…' : "I'm finished — send this to my agent"}
          </button>
          <span className={`insured-saved is-${saved}`}>
            {saved === 'saving' && 'Saving…'}
            {saved === 'saved' && 'Saved'}
            {saved === 'error' && "Couldn't save — check your connection"}
          </span>
        </div>
        {/* Said plainly, because someone will stop halfway and want to know
            whether they have to start over. They don't. */}
        <p className="supp-note">
          {progress.answered} of {progress.total} answered. You can close this and come back to the
          same link later — nothing is lost. Anything you don&apos;t know, leave blank and your agent
          will follow up.
        </p>
        {error && <div className="insured-error">{error}</div>}
      </div>
    </div>
  );
}
